/**
 * Light, dark or blue, and remembering which.
 *
 * The VALUE is applied to `<html>` before first paint by a snippet in
 * index.html; this module is the part that runs afterwards — reading what was
 * chosen, changing it, and writing it down. Split that way because the
 * pre-paint snippet cannot be a module: waiting for one is the flash it
 * exists to prevent.
 *
 * Three states, not two. "Follow the system" is a real answer and it is the
 * default, so a viewer who has never chosen gets the theme their machine
 * says, and one who HAS chosen keeps it even when the machine disagrees.
 * Collapsing that into a boolean would make the first press permanent.
 *
 * Blue is never what the system gets. No operating system has a blue
 * preference, so it exists only once somebody has picked it.
 */

export const THEMES = ['light', 'dark', 'blue'] as const;
export type Theme = (typeof THEMES)[number];
export type ThemeChoice = Theme | 'system';

/** One key, read by both apps, so a choice here is not undone over there. */
export const THEME_KEY = 'tomo:theme';

export function readChoice(storage: Pick<Storage, 'getItem'>): ThemeChoice {
  try {
    const saved = storage.getItem(THEME_KEY);
    return isTheme(saved) ? saved : 'system';
  } catch {
    // Blocked storage is "no choice recorded", not a failure to report.
    return 'system';
  }
}

/** What to actually paint, given a choice and what the machine says. */
export function resolveTheme(choice: ThemeChoice, systemPrefersLight: boolean): Theme {
  if (choice !== 'system') return choice;
  return systemPrefersLight ? 'light' : 'dark';
}

export function isTheme(value: unknown): value is Theme {
  return (THEMES as readonly unknown[]).includes(value);
}

export function applyTheme(theme: Theme, root: { dataset: DOMStringMap }): void {
  root.dataset.theme = theme;
}

export function saveChoice(
  choice: Theme,
  storage: Pick<Storage, 'setItem'>,
): void {
  try {
    storage.setItem(THEME_KEY, choice);
  } catch {
    // The theme still applies for this page; it just will not be remembered.
  }
}
